/**
 * Sprint 17 B1: Channel-specific message nodes (SMS, WhatsApp, DM)
 * Each renders with a platform indicator and compliance badge.
 */
import { useTranslation } from "@/i18n";
import { cn } from "@/lib/utils";
import { Smartphone, MessageCircle, Send } from "lucide-react";
import type { SmsNodeData, WhatsAppNodeData, DmNodeData } from "./types";

interface ChannelNodeProps<T> {
  node: T;
  isSelected: boolean;
  onSelect: (id: string) => void;
}

export function SmsNode({ node, isSelected, onSelect }: ChannelNodeProps<SmsNodeData>) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={() => onSelect(node.id)}
      className={cn(
        "flex flex-col gap-1 rounded-lg border-2 px-4 py-3 text-left shadow-sm transition-colors min-w-[200px]",
        "bg-[var(--color-info)]/5 border-[var(--color-info)]/20 hover:border-[var(--color-info)]/50",
        isSelected && "ring-2 ring-[var(--color-info)] border-[var(--color-info)]/80",
      )}
    >
      <div className="flex items-center gap-2">
        <Smartphone className="h-4 w-4 text-[var(--color-info)]" />
        <span className="text-xs font-medium text-[var(--color-info)] uppercase">
          {t("journey.node.sms")}
        </span>
      </div>
      {node.body && (
        <div className="text-xs text-[var(--color-muted-foreground)] truncate max-w-[240px]">
          {node.body.slice(0, 80)}{node.body.length > 80 ? "..." : ""}
        </div>
      )}
    </button>
  );
}

export function WhatsAppNode({ node, isSelected, onSelect }: ChannelNodeProps<WhatsAppNodeData>) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={() => onSelect(node.id)}
      className={cn(
        "flex flex-col gap-1 rounded-lg border-2 px-4 py-3 text-left shadow-sm transition-colors min-w-[200px]",
        "bg-[var(--color-success)]/5 border-[var(--color-success)]/20 hover:border-[var(--color-success)]/50",
        isSelected && "ring-2 ring-[var(--color-success)] border-[var(--color-success)]/80",
      )}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MessageCircle className="h-4 w-4 text-[var(--color-success)]" />
          <span className="text-xs font-medium text-[var(--color-success)] uppercase">
            {t("journey.node.whatsapp")}
          </span>
        </div>
        {node.templateId && (
          <span className="text-[10px] bg-[var(--color-success)]/10 text-[var(--color-success)] rounded px-1.5 py-0.5">
            Template
          </span>
        )}
      </div>
      {node.body && (
        <div className="text-xs text-[var(--color-muted-foreground)] truncate max-w-[240px]">
          {node.body.slice(0, 80)}{node.body.length > 80 ? "..." : ""}
        </div>
      )}
    </button>
  );
}

export function DmNode({ node, isSelected, onSelect }: ChannelNodeProps<DmNodeData>) {
  const platformLabel = node.platform.charAt(0).toUpperCase() + node.platform.slice(1);

  return (
    <button
      type="button"
      onClick={() => onSelect(node.id)}
      className={cn(
        "flex flex-col gap-1 rounded-lg border-2 px-4 py-3 text-left shadow-sm transition-colors min-w-[200px]",
        "bg-[var(--color-ai)]/5 border-[var(--color-ai)]/20 hover:border-[var(--color-ai)]/50",
        isSelected && "ring-2 ring-[var(--color-ai)] border-[var(--color-ai)]/80",
      )}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Send className="h-4 w-4 text-[var(--color-ai)]" />
          <span className="text-xs font-medium text-[var(--color-ai)] uppercase">
            {platformLabel} DM
          </span>
        </div>
      </div>
      {node.body && (
        <div className="text-xs text-[var(--color-muted-foreground)] truncate max-w-[240px]">
          {node.body.slice(0, 80)}{node.body.length > 80 ? "..." : ""}
        </div>
      )}
      {node.quickReplies && node.quickReplies.length > 0 && (
        <div className="flex gap-1 mt-1 flex-wrap">
          {node.quickReplies.slice(0, 3).map((reply) => (
            <span key={reply} className="text-[10px] bg-[var(--color-ai)]/10 text-[var(--color-ai)] rounded px-1.5 py-0.5">
              {reply}
            </span>
          ))}
        </div>
      )}
    </button>
  );
}
